import React from 'react';
import { Link } from 'react-router-dom';
import { Target, ArrowLeft, MapPin, Award, Rocket } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const FounderPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead title="About the Founder — O4S.ai" path="/founder" />

      <nav className="glass-nav sticky top-0 z-50 px-4 md:px-8 py-3 border-b border-earth-200">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2 text-earth-500 hover:text-earth-700 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Target className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-earth-700">O4S.ai</span>
          </Link>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 md:px-8 py-10 md:py-16">
        <h1 className="text-3xl sm:text-4xl font-bold text-earth-700 mb-2">About the Founder</h1>
        <p className="text-earth-500 text-lg mb-10">The vision behind O4S.ai</p>

        <section className="bg-white rounded-2xl border border-earth-200 p-6 md:p-8 mb-8">
          <div className="flex flex-col sm:flex-row items-start gap-6">
            <div className="w-20 h-20 bg-secondary rounded-2xl flex items-center justify-center flex-shrink-0">
              <span className="text-3xl font-bold text-white">SS</span>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-earth-700 mb-1">S. Subramanyam & Team</h2>
              <p className="text-primary font-semibold mb-1">Founder, O4S.ai</p>
              <div className="flex items-center gap-1.5 text-sm text-earth-500 mb-4">
                <MapPin className="w-3.5 h-3.5" />
                <span>Hyderabad, Telangana, India</span>
              </div>
              <p className="text-earth-600 leading-relaxed mb-4">S. Subramanyam is a digital marketer and lifelong learner based in Hyderabad. As the founder of an AI-Powered Digital Marketing Academy and KKM.Digital, he brings deep expertise in technology, education, and digital strategy.</p>
              <p className="text-earth-600 leading-relaxed">O4S.ai was conceptualised from a simple belief: every student in India, especially in Andhra Pradesh and Telangana, should have access to quality career guidance that is personalised, data-driven, and actionable.</p>
            </div>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-bold text-earth-700 mb-4">Why O4S.ai Was Built</h2>
          <div className="space-y-4">
            {[
              { icon: Award, title: 'Bridging the Gap', desc: 'Most students in tier-2 and tier-3 cities lack access to quality career counselling. O4S.ai brings expert-level guidance to every student with an internet connection.' },
              { icon: Rocket, title: 'AI Meets Local Knowledge', desc: 'By combining AI with hyper-local insights about AP and Telangana job markets, colleges, and salary data, O4S.ai delivers recommendations that are truly relevant.' },
              { icon: Target, title: 'Actionable, Not Just Informational', desc: 'Rather than generic advice, O4S.ai provides 90-day action plans, skill recommendations, and income roadmaps that students can act on immediately.' },
            ].map((item, i) => (
              <div key={i} className="flex gap-4 bg-white rounded-xl border border-earth-200 p-5">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-bold text-earth-700 mb-1">{item.title}</h3>
                  <p className="text-sm text-earth-500 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="bg-surface-alt rounded-xl border border-earth-200 p-6 text-center">
          <p className="text-earth-600 italic text-sm leading-relaxed">"I used O4S.ai for my career growth, and the AI-guided recommendations were extremely helpful, a must-try tool for taking your career to the next level."</p>
        </div>
      </main>

      <footer className="px-4 md:px-8 py-8 bg-earth-700 text-white mt-auto">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm mb-4">
            <Link to="/about" className="text-earth-400 hover:text-white transition-colors">About</Link>
            <Link to="/founder" className="text-earth-400 hover:text-white transition-colors">Founder</Link>
            <Link to="/contact" className="text-earth-400 hover:text-white transition-colors">Contact</Link>
            <Link to="/privacy" className="text-earth-400 hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="text-earth-400 hover:text-white transition-colors">Terms</Link>
            <Link to="/legal" className="text-earth-400 hover:text-white transition-colors">Legal</Link>
          </div>
          <p className="text-earth-500 text-xs text-center">&copy; 2026 O4S.ai. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default FounderPage;
